'use client';
import React from 'react';
import { Box } from '@mui/material';
import { InfiniteData } from '@tanstack/react-query';

import { ErrorState } from '@/components/common';
import { CommentData, CommentsResponse } from '@/types';

import { COMMENTS_SKELETON_COUNT } from '@/constants';

export interface CommentListProps {
  data: InfiniteData<CommentsResponse> | undefined;
  isLoading: boolean;
  isError: boolean;
  isFetchingNextPage: boolean;
  observerTarget: React.RefObject<HTMLDivElement>;
  onRetry?: () => void;
  renderItem: (comment: CommentData) => React.ReactNode;
  renderSkeleton: (index: number) => React.ReactNode;
  renderLoadingMoreSkeleton: () => React.ReactNode;
  EmptyState: React.ComponentType;
}

const getPageComments = (page: CommentsResponse) => page.data || [];

export function CommentList({
  data,
  isLoading,
  isError,
  isFetchingNextPage,
  observerTarget,
  onRetry,
  renderItem,
  renderSkeleton,
  renderLoadingMoreSkeleton,
  EmptyState,
}: CommentListProps) {
  if (isLoading) {
    const skeletons = Array.from({ length: COMMENTS_SKELETON_COUNT }).map((_, i) =>
      renderSkeleton(i)
    );
    return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>{skeletons}</Box>;
  }

  if (isError) {
    return <ErrorState message="Failed to load comments" onRetry={onRetry} />;
  }

  const allComments = data?.pages?.flatMap(getPageComments) || [];

  if (allComments.length === 0) {
    return <EmptyState />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
      {allComments.map(renderItem)}
      {isFetchingNextPage && renderLoadingMoreSkeleton()}
      <div ref={observerTarget} style={{ height: '10px' }} />
    </Box>
  );
}

export default CommentList;
